import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';
import { Box, Container, Typography, Button, CircularProgress } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

// Theme
import theme from './theme';

// Layout
import Layout from './components/Layout';

// Existing Pages (eager loaded)
import HomePage from './pages/HomePage';
import RisingSignLanding from './pages/RisingSignLanding';
import MonthlyForecast from './pages/MonthlyForecast';
import MonthlyDetail from './pages/MonthlyDetail';
import CosmicOverview from './pages/CosmicOverview';
import EventPage from './pages/EventPage';
import PricingPage from './pages/PricingPage';
import SignSelectionPage from './pages/SignSelectionPage';
import CheckoutPage from './pages/CheckoutPage';
import ThankYouPage from './pages/ThankYouPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import MembersDashboard from './pages/MembersDashboard';
import VideoPlayer from './pages/VideoPlayer';
import BirthChartPage from './pages/BirthChartPage';
import FreeBirthChartPage from './pages/FreeBirthChartPage';
import TransitCalendarPage from './pages/TransitCalendarPage';
import NatalToTransitCalendarPage from './pages/NatalToTransitCalendarPage';
import NatalTransitCalendarPage from './pages/NatalTransitCalendarPage';
import TransitCalendarRegister from './pages/TransitCalendarRegister';
import TransitCalendarLogin from './pages/TransitCalendarLogin';
import ProfileSelection from './pages/ProfileSelection';
import AddProfile from './pages/AddProfile';

// Context
import { UserProvider } from './context/UserContext';
import { AnalyticsProvider } from './context/AnalyticsContext';

// New Pages (lazy loaded)
const MyStarsPage = React.lazy(() => import('./pages/MyStarsPage'));
const ExplorePage = React.lazy(() => import('./pages/ExplorePage'));
const GiftShopPage = React.lazy(() => import('./pages/GiftShopPage'));
const ClaudiaPage = React.lazy(() => import('./pages/ClaudiaPage'));
const NerdZonePage = React.lazy(() => import('./pages/NerdZonePage'));
const CompatibilityPage = React.lazy(() => import('./pages/CompatibilityPage'));
const CompatibilityReportPage = React.lazy(() => import('./pages/CompatibilityReportPage'));
const YearAhead2027Page = React.lazy(() => import('./pages/YearAhead2027Page'));
const GiftRedeemPage = React.lazy(() => import('./pages/GiftRedeemPage'));
const VenusReadingPage = React.lazy(() => import('./pages/VenusReadingPage'));
const VenusReadingThankYouPage = React.lazy(() => import('./pages/VenusReadingThankYouPage'));
const VenusComingSoonPage = React.lazy(() => import('./pages/VenusComingSoonPage'));
// The Personal Venus Star Point Reading's order backend is not deployed yet: until launch every Venus URL shows the
// coming-soon page (Claudia, 2026-10-07). Flip to true only once /vsp-order + webhook are live.
const VSP_READING_LIVE = true;

// /venus: short link for the YouTube description; keeps any query and tags the source
// as youtube unless the link already carries one.
const VenusAlias = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (!params.has('utm_source') && !params.has('src')) params.set('src', 'youtube');
  return <Navigate to={`/venus-star-point-reading?${params.toString()}`} replace />;
};

// Suspense fallback
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      bgcolor: '#0a0a0f',
    }}
  >
    <CircularProgress sx={{ color: '#c9a87c' }} />
  </Box>
);

// Protected Dashboard Component
const ProtectedDashboard = () => {
  const isDashboardEnabled = () => {
    const saved = localStorage.getItem('dashboard-enabled');
    if (saved) {
      return JSON.parse(saved);
    }
    return false; // Default: disabled for public users
  };

  if (!isDashboardEnabled()) {
    return (
      <Box sx={{ bgcolor: '#0a0a0f', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <LockIcon sx={{ fontSize: 80, color: '#c9a87c', mb: 3 }} />
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: '#ede8e0' }}>
              Coming Soon
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              The Members Dashboard is currently under development and will be available soon.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.location.href = '/'}
              sx={{
                mt: 3,
                background: 'linear-gradient(135deg, #c9a87c, #a88a5e)',
                color: '#0a0a0f',
                fontWeight: 700,
                '&:hover': { background: 'linear-gradient(135deg, #dbc4a0, #c9a87c)' },
              }}
            >
              Return to Home
            </Button>
          </Box>
        </Container>
      </Box>
    );
  };

  return <MembersDashboard />;
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
          <UserProvider>
            <AnalyticsProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {/* Main pages */}
                      <Route path="/" element={<HomePage />} />
                      <Route path="/my-stars" element={<MyStarsPage />} />
                      <Route path="/explore" element={<ExplorePage />} />
                      <Route path="/gift-shop" element={<GiftShopPage />} />
                      <Route path="/claudia" element={<ClaudiaPage />} />
                      <Route path="/nerd-zone" element={<NerdZonePage />} />
                      <Route path="/compatibility" element={<CompatibilityPage />} />
                      <Route path="/compatibility/report/:reportId" element={<CompatibilityReportPage />} />
                      <Route path="/gift/redeem/:giftCode" element={<GiftRedeemPage />} />

                      {/* Personal Venus Star Point reading (not linked in the nav until launch) */}
                      <Route path="/venus-star-point-reading" element={VSP_READING_LIVE ? <VenusReadingPage /> : <VenusComingSoonPage />} />
                      <Route path="/venus-star-point-reading/thank-you" element={VSP_READING_LIVE ? <VenusReadingThankYouPage /> : <VenusComingSoonPage />} />
                      <Route path="/venus" element={VSP_READING_LIVE ? <VenusAlias /> : <VenusComingSoonPage />} />

                      {/* Existing routes (unchanged) */}
                      <Route path="/rising/:sign" element={<RisingSignLanding />} />
                      <Route path="/rising/:sign/overview" element={<CosmicOverview />} />
                      <Route path="/rising/:sign/monthly" element={<MonthlyForecast />} />
                      <Route path="/rising/:sign/monthly/:month" element={<MonthlyDetail />} />
                      <Route path="/rising/:sign/:event" element={<EventPage />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      {/* Clean, shareable links straight to the Shop (skip Subscriptions) */}
                      <Route path="/shop" element={<Navigate to="/pricing?section=shop" replace />} />
                      <Route path="/pricing/shop" element={<Navigate to="/pricing?section=shop" replace />} />
                      <Route path="/year-ahead-2027" element={<YearAhead2027Page />} />
                      <Route path="/shop/forecasts" element={<Navigate to="/pricing?section=forecasts" replace />} />
                      <Route path="/shop/calendar" element={<Navigate to="/pricing?section=calendar" replace />} />
                      <Route path="/select-signs" element={<SignSelectionPage />} />
                      <Route path="/checkout" element={<CheckoutPage />} />
                      <Route path="/thank-you" element={<ThankYouPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/dashboard" element={<ProtectedDashboard />} />
                      <Route path="/watch/:videoId" element={<VideoPlayer />} />
                      <Route path="/birth-chart" element={<BirthChartPage />} />
                      <Route path="/free-birth-chart" element={<FreeBirthChartPage />} />
                      <Route path="/transit-calendar" element={<TransitCalendarPage />} />
                      <Route path="/natal-to-transit-calendar" element={<NatalToTransitCalendarPage />} />
                      <Route path="/natal-transit-calendar" element={<NatalTransitCalendarPage />} />
                      <Route path="/transit-calendar/register" element={<TransitCalendarRegister />} />
                      <Route path="/transit-calendar/login" element={<TransitCalendarLogin />} />
                      <Route path="/transit-calendar/profiles" element={<ProfileSelection />} />
                      <Route path="/transit-calendar/add-profile" element={<AddProfile />} />
                    </Routes>
                  </Suspense>
                </Layout>
              </Router>
            </AnalyticsProvider>
          </UserProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
